import React, { useMemo, useRef, useState } from "react";
import { HashRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Button, Typography } from "@mui/material";
import {
  WebProfitPromoBanner,
  WebProfitPromoFooter,
  WebProfitPromoUnavailable,
} from "./components/WebProfitPromotion";
import { shouldShowWebProfitPromotion } from "./utils/webProfitPromotion";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { PaymentProvider } from "./context/PaymentContext";
import {
  ProjectConfigProvider,
  useProjectConfig,
} from "./context/ProjectConfigContext";
import { LandingProvider, useLanding } from "./context/LandingContext";

import "./index.css";
import HomePage from "./pages/HomePage";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import CartDrawer from "./components/cart/CartDrawer";
import ModuleGate from "./components/modules/ModuleGate";
import WebPushPrompt from "./components/WebPushPrompt";
import StorefrontActivityTracker from "./components/StorefrontActivityTracker";
import { createStorefrontTheme } from "./theme/createStorefrontTheme";
import StorefrontLoadingScreen from "./components/system/StorefrontLoadingScreen";
import StorefrontPendingBar from "./components/system/StorefrontPendingBar";
import ServiceUpdateBoundary from "./components/system/ServiceUpdateBoundary";
import StorefrontFatalBoundary from "./components/system/StorefrontFatalBoundary";

// Las rutas secundarias no deben penalizar el arranque de la portada.
// Webpack/CRA crea chunks separados y sólo los descarga cuando se navega a esa ruta.
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const ProductPage = React.lazy(() => import("./pages/ProductPage"));
const ProductsPage = React.lazy(() => import("./pages/ProductsPage"));
const GastronomicMenuPage = React.lazy(() => import("./pages/GastronomicMenuPage"));
const PropertiesPage = React.lazy(() => import("./pages/PropertiesPage"));
const PropertyPage = React.lazy(() => import("./pages/PropertyPage"));
const BookingPage = React.lazy(() => import("./pages/BookingPage"));
const CartPage = React.lazy(() => import("./pages/CartPage"));
const CheckoutPage = React.lazy(() => import("./pages/CheckoutPage"));
const OrderResultPage = React.lazy(() => import("./pages/OrderResultPage"));
const PaymentResultPage = React.lazy(() => import("./pages/PaymentResultPage"));
const CustomerMembershipsPage = React.lazy(() => import("./pages/CustomerMembershipsPage"));
const CustomerAppointmentsPage = React.lazy(() => import("./pages/CustomerAppointmentsPage"));

function formatMessage(template, businessName) {
  return String(template || "").replace(/\{businessName\}/g, businessName ?? "El sitio");
}

function LandingUnavailable({
  maintenance,
  businessName,
  messages,
  previewRequested = false,
  error = "",
  onRetry,
  onExitPreview,
  showWebProfitPromotion = false,
}) {
  const title = previewRequested
    ? "No se pudo abrir la vista previa"
    : maintenance
      ? messages?.maintenanceTitle
      : messages?.unavailableTitle;
  const description = previewRequested
    ? error || "La autorización temporal no está disponible."
    : maintenance
      ? formatMessage(messages?.maintenanceDescription, businessName)
      : formatMessage(messages?.unavailableDescription, businessName);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ maxWidth: 620, textAlign: "center" }}>
        {title ? (
          <Typography variant="h3" sx={{ fontWeight: 950, color: "text.primary" }}>
            {title}
          </Typography>
        ) : null}
        {description ? (
          <Typography sx={{ mt: title ? 1.5 : 0, color: "text.secondary", lineHeight: 1.7 }}>
            {description}
          </Typography>
        ) : null}
        {showWebProfitPromotion ? <WebProfitPromoUnavailable /> : null}
        {previewRequested ? (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 1.25,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {typeof onRetry === "function" ? (
              <Button variant="contained" onClick={onRetry}>
                Reintentar
              </Button>
            ) : null}
            {typeof onExitPreview === "function" ? (
              <Button variant="outlined" onClick={onExitPreview}>
                Salir de la vista previa
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function previewExpirationLabel(value) {
  const timestamp = Date.parse(String(value || ""));
  if (!Number.isFinite(timestamp)) return "";

  try {
    return new Intl.DateTimeFormat("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(timestamp));
  } catch {
    return "";
  }
}

function PreviewSessionBadge({
  revision,
  publishedRevision,
  publicStatus,
  hasUnpublishedChanges,
  expiresAt,
  onExit,
}) {
  const expiration = previewExpirationLabel(expiresAt);
  const publicVersionLabel = !publishedRevision
    ? "todavía no existe una versión publicada"
    : publicStatus === "maintenance"
      ? `mantenimiento · última publicación P${publishedRevision}`
      : publicStatus === "draft"
        ? `sitio oculto · última publicación P${publishedRevision}`
        : `versión P${publishedRevision}`;
  const previewDifferenceLabel = hasUnpublishedChanges
    ? "Este borrador contiene cambios que el público todavía no ve."
    : "Este borrador coincide con la versión publicada.";

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        zIndex: 1500,
        right: { xs: 12, sm: 20 },
        bottom: { xs: 12, sm: 20 },
        maxWidth: "calc(100vw - 24px)",
        px: 1.5,
        py: 1.25,
        borderRadius: 2.5,
        bgcolor: "background.paper",
        color: "text.primary",
        border: "1px solid",
        borderColor: "warning.main",
        boxShadow: 8,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, fontSize: 13, lineHeight: 1.2 }}>
          Vista previa privada{revision ? ` · borrador B${revision}` : ""}
        </Typography>
        <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: 11.5, lineHeight: 1.35 }}>
          Sólo esta pestaña muestra el borrador. Público: {publicVersionLabel}. {previewDifferenceLabel}
          {expiration ? ` Vence ${expiration}.` : ""}
        </Typography>
      </Box>
      <Button size="small" variant="outlined" onClick={onExit} sx={{ flexShrink: 0 }}>
        Salir del preview
      </Button>
    </Box>
  );
}

function Storefront({ language, setLanguage }) {
  const {
    landingEnabled,
    ordersEnabled,
    gastronomicMenuEnabled,
    access,
    accessVerified,
    loading: projectConfigLoading,
  } = useProjectConfig();
  const { loading: authLoading } = useAuth();
  const [promoHeight, setPromoHeight] = useState(0);
  const {
    maintenance,
    publicationAvailable,
    business,
    theme,
    messages,
    sections,
    loading,
    usingFallback,
    error,
    previewActive,
    previewRequested,
    previewRevision,
    previewPublishedRevision,
    previewPublicStatus,
    previewHasUnpublishedChanges,
    previewExpiresAt,
    refreshLanding,
    exitPreview,
  } = useLanding();

  const productUnavailable = {
    modules: ["products"],
    title: messages?.catalogDisabledTitle,
    message: messages?.catalogDisabledDescription,
  };
  const ordersUnavailable = {
    modules: ["orders"],
    title: messages?.ordersDisabledTitle,
    message: messages?.ordersDisabledDescription,
  };
  const ecommerceUnavailable = {
    modules: ["ecommerce"],
    title: messages?.ecommerceDisabledTitle,
    message: messages?.ecommerceDisabledDescription,
  };

  // El loader de pantalla completa pertenece solamente al bootstrap real.
  // Una vez que el storefront pudo resolver su primera vista, los refreshes de
  // sesión/config/contenido deben ocurrir por detrás sin desmontar la página.
  const bootstrapPendingRef = useRef(true);
  const bootstrapLoading = bootstrapPendingRef.current && (loading || projectConfigLoading || authLoading);
  const unavailable = previewRequested
    ? !publicationAvailable
    : !landingEnabled || !publicationAvailable || maintenance;
  const showPromotion = shouldShowWebProfitPromotion({
    access,
    accessVerified,
    previewRequested: previewRequested || previewActive,
  });
  const hasNavbar = sections?.showNavbar !== false;

  React.useEffect(() => {
    // Las anclas (contacto, catálogo, etc.) ya tienen el offset de Navbar.
    // Agregamos sólo la altura variable del aviso para que al navegar a una
    // sección su encabezado no quede detrás del AppBar fijo.
    if (typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const original = root.style.scrollPaddingTop;
    if (showPromotion && hasNavbar && !unavailable && promoHeight > 0) {
      root.style.scrollPaddingTop = `${promoHeight}px`;
    }
    return () => { root.style.scrollPaddingTop = original; };
  }, [showPromotion, hasNavbar, unavailable, promoHeight]);

  if (bootstrapLoading) {
    const brandReady = !usingFallback;
    return (
      <StorefrontLoadingScreen
        businessName={brandReady ? business?.name : ""}
        logoUrl={brandReady ? (theme?.compactLogoUrl || theme?.logoUrl || theme?.faviconUrl) : ""}
      />
    );
  }
  bootstrapPendingRef.current = false;

  const promoBanner = showPromotion ? (
    <WebProfitPromoBanner onHeightChange={hasNavbar && !unavailable ? setPromoHeight : undefined} />
  ) : null;

  return (
    <HashRouter>
      {unavailable ? (
        <LandingUnavailable
          maintenance={maintenance}
          businessName={business?.name}
          messages={messages}
          previewRequested={previewRequested}
          error={error}
          onRetry={previewActive ? refreshLanding : null}
          onExitPreview={previewRequested ? exitPreview : null}
          showWebProfitPromotion={showPromotion}
        />
      ) : (
        <>
          {hasNavbar ? (
            <>
              <Navbar language={language} setLanguage={setLanguage} promoBanner={promoBanner} />
              {/* Navbar es fixed; esta reserva exacta evita tapar el contenido. */}
              {showPromotion ? <Box aria-hidden="true" sx={{ height: `${promoHeight}px` }} /> : null}
            </>
          ) : promoBanner}
          {ordersEnabled ? <CartDrawer /> : null}

          <React.Suspense fallback={<StorefrontPendingBar label="Abriendo sección" />}>
            <Routes>
            <Route path="/" element={<HomePage language={language} />} />
            <Route path="/contact" element={<ContactPage language={language} />} />
            <Route
              path="/reservar/:id"
              element={
                <ModuleGate {...productUnavailable}>
                  <BookingPage />
                </ModuleGate>
              }
            />
            <Route path="/login" element={<LoginPage language={language} />} />
            <Route path="/register" element={<RegisterPage language={language} />} />
            <Route path="/mi-cuenta/turnos" element={<CustomerAppointmentsPage />} />
            <Route path="/mi-cuenta/mensualidades" element={<CustomerMembershipsPage />} />
            <Route
              path="/producto/:id"
              element={
                <ModuleGate {...productUnavailable}>
                  <ProductPage language={language} />
                </ModuleGate>
              }
            />
            <Route
              path="/productos"
              element={
                <ModuleGate {...productUnavailable}>
                  <ProductsPage language={language} />
                </ModuleGate>
              }
            />
            <Route path="/propiedades" element={<PropertiesPage />} />
            <Route path="/propiedad/:id" element={<PropertyPage />} />
            {gastronomicMenuEnabled ? (
              <Route
                path="/carta"
                element={
                  <ModuleGate {...productUnavailable}>
                    <GastronomicMenuPage />
                  </ModuleGate>
                }
              />
            ) : null}
            <Route
              path="/carrito"
              element={
                <ModuleGate {...ordersUnavailable}>
                  <CartPage />
                </ModuleGate>
              }
            />
            <Route
              path="/checkout"
              element={
                <ModuleGate {...ordersUnavailable}>
                  <CheckoutPage />
                </ModuleGate>
              }
            />
            <Route
              path="/pedido/confirmado"
              element={
                <ModuleGate {...ordersUnavailable}>
                  <OrderResultPage />
                </ModuleGate>
              }
            />
            <Route
              path="/pago/:status"
              element={
                <ModuleGate {...ecommerceUnavailable}>
                  <PaymentResultPage />
                </ModuleGate>
              }
            />
            </Routes>
          </React.Suspense>

          {sections?.showFooter !== false ? <Footer /> : null}

          {/* Fuera del Footer editable: la atribución no se puede ocultar. */}
          {showPromotion ? (
            <WebProfitPromoFooter isTrial={access?.mode === "full" && access?.billingStatus === "trial"} />
          ) : null}
          {!previewActive ? <StorefrontActivityTracker /> : null}
          {!previewActive ? <WebPushPrompt /> : null}
          {previewActive ? (
            <PreviewSessionBadge
              revision={previewRevision}
              publishedRevision={previewPublishedRevision}
              publicStatus={previewPublicStatus}
              hasUnpublishedChanges={previewHasUnpublishedChanges}
              expiresAt={previewExpiresAt}
              onExit={exitPreview}
            />
          ) : null}
        </>
      )}
    </HashRouter>
  );
}

function DynamicStoreApp() {
  const [language, setLanguage] = useState("English");
  const { theme: landingTheme } = useLanding();

  const theme = useMemo(
    () => createStorefrontTheme(landingTheme),
    [landingTheme]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <CartProvider>
          <PaymentProvider>
            <Storefront language={language} setLanguage={setLanguage} />
          </PaymentProvider>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <StorefrontFatalBoundary>
      <ServiceUpdateBoundary>
        <ProjectConfigProvider>
          <LandingProvider>
            <DynamicStoreApp />
          </LandingProvider>
        </ProjectConfigProvider>
      </ServiceUpdateBoundary>
    </StorefrontFatalBoundary>
  );
}

export default App;
